import { Component, OnInit, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatDialogRef } from '@angular/material/dialog';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Clipboard } from '@angular/cdk/clipboard';
import { DialogDataService } from '../services/dialog-data.service';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'kt-dialog-import',
  templateUrl: './dialog-import.component.html',
  styleUrls: ['./dialog-import.component.scss'],
})
export class DialogImportComponent implements OnInit {
  form: FormGroup;

  popupsDropdown: any[] = [];
  popupsDropdownSettings = {
    singleSelection: true,
    text: 'Please Select',
    enableFilterSelectAll: false,
    enableSearchFilter: true,
    classes: 'dropdown',
    primaryKey: 'id',
    labelKey: 'display_name',
    showCheckbox: false,
    noDataLabel: 'No popups found'
  };
  selectedPopup: any[] = [];

  selectedFile: File = null;
  loading: boolean = false;
  checkValidation: boolean = false;

  private invalidUsernames: string[] = [];
  private alreadyAssignedUsernames: string[] = [];

  constructor(
    public dialogRef: MatDialogRef<DialogImportComponent>,
    private dialogDataService: DialogDataService,
    private translateService: TranslateService,
    private clipboard: Clipboard,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit() {
    this.formInit();
    this.loadPopups();
  }

  formInit() {
    this.form = new FormGroup({
      popup_id: new FormControl(null, Validators.required),
    });
  }

  loadPopups() {
    this.dialogDataService.getWithQuery('?paginate=false&status=1').subscribe(
      (res: any) => {
        this.popupsDropdown = res.map((p: any) => ({
          id: p.id,
          display_name: `${p.code} — ${p.label || ''}`
        }));
      }
    );
  }

  onSelectedPopupChange(items: any[]) {
    if (items && items.length > 0) {
      this.form.patchValue({ popup_id: items[0].id });
    } else {
      this.form.patchValue({ popup_id: null });
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    }
  }

  get canImport(): boolean {
    return this.form.valid && this.selectedFile !== null && !this.loading;
  }

  onImport() {
    this.checkValidation = true;
    if (!this.canImport) return;

    this.loading = true;
    const formData = new FormData();
    formData.append('popup_id', this.form.value.popup_id.toString());
    formData.append('file', this.selectedFile);

    this.dialogDataService.importDialogLogs(formData).subscribe(
      (res: any) => {
        this.loading = false;
        this.onSwal(res.data);
      },
      (err) => {
        this.loading = false;
        const message = err.error?.message?.[0] || 'Import failed. Please try again.';
        Swal.fire({
          title: this.translateService.instant('Import Failed'),
          text: message,
          icon: 'error',
          heightAuto: false
        });
      }
    );
  }

  onSwal(data: any) {
    this.invalidUsernames = data.invalid_usernames || [];
    this.alreadyAssignedUsernames = data.already_assigned_usernames || [];

    const importedCount = data.imported_count;
    const invalidCount = data.invalid_count;
    const alreadyAssignedCount = data.already_assigned_count;

    const icon: SweetAlertIcon = importedCount > 0 ? 'success' : 'info';
    const iconColor = importedCount === 0 && (invalidCount > 0 || alreadyAssignedCount > 0) ? '#f27474' : '';

    let html = `<div class="text-center w-100">${importedCount} member(s) imported successfully.</div>`;

    if (invalidCount > 0) {
      const copyIcon = `<i class="fas fa-copy copy-icon copy-invalid-usernames"></i><div class="swal2-copy-tooltip copy-invalid-usernames-tooltip">Copied!</div>`;
      html += `<div class="text-left mx-2 mt-3">Usernames Not Found: ${copyIcon}</div><div class="failed-records-section p-2 m-2"><ul>`;
      this.invalidUsernames.forEach(u => html += `<li>${u}</li>`);
      html += `</ul></div>`;
    }

    if (alreadyAssignedCount > 0) {
      const copyIcon = `<i class="fas fa-copy copy-icon copy-already-assigned"></i><div class="swal2-copy-tooltip copy-already-assigned-tooltip">Copied!</div>`;
      html += `<div class="text-left mx-2 mt-3">Members Already Assigned: ${copyIcon}</div><div class="failed-records-section p-2 m-2"><ul>`;
      this.alreadyAssignedUsernames.forEach(u => html += `<li>${u}</li>`);
      html += `</ul></div>`;
    }

    Swal.fire({
      title: this.translateService.instant('Import Complete!'),
      html,
      icon,
      iconColor,
      heightAuto: false,
      didOpen: () => {
        this.bindCopy('.copy-invalid-usernames', '.copy-invalid-usernames-tooltip', this.invalidUsernames);
        this.bindCopy('.copy-already-assigned', '.copy-already-assigned-tooltip', this.alreadyAssignedUsernames);
      }
    }).then(() => {
      this.dialogRef.close(true);
    });
  }

  private bindCopy(iconSelector: string, tooltipSelector: string, list: string[]) {
    const copyIcon = this.document.querySelector(iconSelector);
    if (copyIcon) {
      const tooltip = this.document.querySelector(tooltipSelector) as HTMLElement;
      copyIcon.addEventListener('click', () => {
        this.clipboard.copy(list.join('\n'));
        if (tooltip) {
          tooltip.style.display = 'block';
          setTimeout(() => { tooltip.style.display = 'none'; }, 2000);
        }
      });
    }
  }

  onClose() {
    this.dialogRef.close();
  }
}
